import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Sparkles, ChevronDown, Gauge, MapPin, Crown, GitCompare, Car } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/lease-deals", label: "Lease Deals" },
  { to: "/inventory", label: "Inventory" },
  { to: "/find-my-car", label: "Find A Car" },
  { to: "/financing", label: "Financing" },
  { to: "/trade-in", label: "Trade-In" },
];

const TOOLS = [
  { to: "/matchmaker", label: "AI Matchmaker", icon: Sparkles, desc: "Find your perfect car in 7 questions" },
  { to: "/negotiate", label: "Negotiation AI", icon: Gauge, desc: "Score any dealer quote 0-10" },
  { to: "/real-cost", label: "Real Monthly Cost", icon: Crown, desc: "True ownership cost calculator" },
  { to: "/near-me", label: "Deals Near Me", icon: MapPin, desc: "ZIP-targeted lease offers" },
  { to: "/marketplace-search", label: "Marketplace Search", icon: Sparkles, desc: "Search Cars.com, Autotrader, CarGurus + more" },
  { to: "/dealers", label: "Dealer Ratings", icon: Sparkles, desc: "Vetted dealer network" },
  { to: "/exotic", label: "Exotic & Ultra-Luxury", icon: Crown, desc: "Lamborghini, Ferrari, Rolls-Royce…" },
  { to: "/concierge", label: "Membership", icon: Crown, desc: "Bronze / Silver / Gold" },
];

export default function NavBar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 glass-strong border-b border-white/5"
        data-testid="main-nav"
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-full silver-gradient flex items-center justify-center text-[#050507] font-black text-sm font-display">
              555
            </div>
            <div className="hidden sm:block">
              <div className="font-display font-light text-[15px] tracking-tight leading-none">555 Auto Brokers</div>
              <div className="label-mono text-[9px] tracking-[0.25em] mt-1">LUXURY · CONCIERGE</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-tight transition-colors ${
                    isActive ? "text-white" : "text-gray-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1 text-[13px] text-gray-400 hover:text-white" data-testid="nav-tools-trigger">
                  AI Tools <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#0A0A0E] border-white/10 text-white w-72 p-2">
                {TOOLS.map((t) => (
                  <DropdownMenuItem key={t.to} onClick={() => navigate(t.to)} className="cursor-pointer focus:bg-white/5 py-2.5" data-testid={`nav-tools-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <t.icon className="w-4 h-4 mr-3 text-silver flex-shrink-0" />
                    <div>
                      <div className="text-sm">{t.label}</div>
                      <div className="text-xs text-gray-500">{t.desc}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <NavLink to="/how-it-works" data-testid="nav-link-how-it-works" className={({isActive}) => `text-[13px] ${isActive ? "text-white" : "text-gray-400 hover:text-white"}`}>How</NavLink>
            <NavLink to="/blog" data-testid="nav-link-journal" className={({isActive}) => `text-[13px] ${isActive ? "text-white" : "text-gray-400 hover:text-white"}`}>Journal</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/booking"
              className="hidden md:inline-flex px-4 py-2 rounded-full silver-gradient text-[#050507] text-[12px] font-bold tracking-wide hover:opacity-90 transition"
              data-testid="nav-book-cta"
            >
              Book Consultation
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-9 h-9 rounded-full glass flex items-center justify-center hover:bg-white/10 transition"
                    data-testid="nav-user-menu"
                  >
                    <User className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[#0A0A0E] border-white/10 text-white w-56"
                >
                  <div className="px-2 py-2 text-xs text-gray-400">
                    Signed in as
                    <div className="text-white text-[13px] truncate">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard")}
                    className="cursor-pointer focus:bg-white/5"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/garage")}
                    className="cursor-pointer focus:bg-white/5"
                    data-testid="menu-garage"
                  >
                    <Car className="w-4 h-4 mr-2" /> My Garage
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem
                      onClick={() => navigate("/admin")}
                      className="cursor-pointer focus:bg-white/5"
                      data-testid="menu-admin"
                    >
                      <Shield className="w-4 h-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/"); }}
                    className="cursor-pointer focus:bg-white/5"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="hidden md:inline-flex text-[12px] tracking-wide text-gray-300 hover:text-white"
                data-testid="nav-login-link"
              >
                Sign In
              </Link>
            )}
            <button
              onClick={() => setOpen((s) => !s)}
              className="lg:hidden w-9 h-9 rounded-full glass flex items-center justify-center"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/5 bg-[#050507]/95 backdrop-blur-xl">
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-md text-sm ${isActive ? "bg-white/5 text-white" : "text-gray-400"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="my-2 px-3 label-mono">AI Tools</div>
              {TOOLS.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-tool-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-md text-sm flex items-center gap-2 ${isActive ? "bg-white/5 text-white" : "text-gray-400"}`
                  }
                >
                  <t.icon className="w-4 h-4 text-silver" />
                  {t.label}
                </NavLink>
              ))}
              <NavLink to="/how-it-works" onClick={() => setOpen(false)} className={({isActive}) => `py-3 px-3 rounded-md text-sm ${isActive ? "bg-white/5 text-white" : "text-gray-400"}`}>How It Works</NavLink>
              <NavLink to="/blog" onClick={() => setOpen(false)} className={({isActive}) => `py-3 px-3 rounded-md text-sm ${isActive ? "bg-white/5 text-white" : "text-gray-400"}`}>Journal</NavLink>
              <Link
                to="/booking"
                onClick={() => setOpen(false)}
                className="mt-3 text-center py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm"
              >
                Book Consultation
              </Link>
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="text-center py-3 rounded-full border border-white/10 text-sm mt-2"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile sticky bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass-strong border-t border-white/10">
        <div className="grid grid-cols-4 h-16 text-[10px] tracking-wider">
          {[
            { to: "/", label: "HOME" },
            { to: "/lease-deals", label: "DEALS" },
            { to: "/find-my-car", label: "FIND" },
            { to: user ? "/dashboard" : "/login", label: user ? "ACCOUNT" : "SIGN IN" },
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`bottom-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center justify-center font-bold ${isActive ? "text-white" : "text-gray-500"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
}
